// Plan panel body for the Billing view — the bold tier name over the 2-col
// entitlement facts grid (mock Billing() Plan panel). Read-only: the Stripe
// Portal (the Panel action, owned by the route) handles all mutations.
// Token-injectable for tests.

import { type JSX, Show } from 'solid-js';
import { t, tPlural } from '../i18n';
import { type SeatInfo, UNLIMITED_AI_DEVS, useEntitlements } from '../lib/billing';
import { UNSET } from '../lib/unset';
import { ErrorState } from './ErrorState';
import { Skeleton } from './Skeleton';

/**
 * Human-readable label for a tier slug. Falls back to the raw slug. Exported for
 * the operator console's tier select (`AdminOrgDetail`) and the radar's
 * per-tier account tiles (`AdminStatBand`), so a tier has one name.
 */
export function tierLabel(tier: string): string {
  const key = `billing.tier_${tier.replace(/-/g, '_')}`;
  return t.has(key) ? t(key) : tier;
}

/**
 * Seat-usage line, over the fleet the account is SERVED (`entitlements.ai_devs`),
 * never the seats it BOUGHT: a comped or granted account holds `seats: 0` with
 * `ai_devs >= 1`, and dividing by `seats` read "2 / 0 active" (plan 101 slice 17
 * row 17-9). `ai_devs === 0` means no fleet at all (nothing bought, nothing
 * granted); `UNLIMITED_AI_DEVS` is a fleet with no ceiling (the Fleet header's plan
 * line reads it the same way, `FleetHeader`).
 */
function seatUsageLine(s: SeatInfo): string {
  const fleet = s.entitlements.ai_devs;
  if (fleet === 0) return t('billing.seat_usage_unlimited');
  if (fleet === UNLIMITED_AI_DEVS) {
    return t('billing.seat_usage_value_unlimited', { used: s.used_seats });
  }
  return t('billing.seat_usage_value', { used: s.used_seats, total: fleet });
}

export function BillingPlanCard(props: { token?: string | null }): JSX.Element {
  const query = useEntitlements(props.token ?? null);

  return (
    <Show
      when={query.data}
      fallback={
        query.isError ? (
          <ErrorState
            message={t('billing.entitlements_error')}
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <div role="status">
            <span class="sr-only">{t('billing.entitlements_loading')}</span>
            <Skeleton height="7rem" />
          </div>
        )
      }
    >
      {(s) => (
        <>
          {/* The plan the account is SERVED as — `solo` for one an operator comped a seat. */}
          <p class="billing-plan__name">{tierLabel(s().served_tier ?? s().tier)}</p>
          <dl class="facts">
            <div>
              <dt>{t('billing.seats')}</dt>
              <dd class="facts__num">
                {s().seats === 0
                  ? UNSET
                  : tPlural('billing.seats_value', s().seats, { seats: s().seats })}
              </dd>
            </div>
            <div>
              <dt>{t('billing.seat_usage')}</dt>
              <dd class="facts__num">{seatUsageLine(s())}</dd>
            </div>
            {/* Any fleet, the unlimited `-1` sentinel included. */}
            <Show when={s().entitlements.ai_devs !== 0}>
              <div>
                <dt>{t('billing.review_enabled')}</dt>
                <dd>{s().entitlements.review_enabled ? t('billing.review_enabled_yes') : UNSET}</dd>
              </div>
            </Show>
            <div>
              <dt>{t('billing.queue')}</dt>
              <dd>{t(`billing.queues_${s().entitlements.queues}`)}</dd>
            </div>
          </dl>
        </>
      )}
    </Show>
  );
}
